import {
  getFavoriteDashboardsV25,
  getFavoriteGraphsV25,
  getFavoriteViewsV25,
  getGraphData,
  getHomeSummaryV25,
} from '../api';
import * as actionTypes from './actionTypes';
import { reqRecupererDonneesGraphe } from './graphe';

export const enregistrerGraphesFavoris = (favorite_graphs_list) => ({
  type: actionTypes.ENREGISTRER_GRAPHE_FAVORI,
  favorite_graphs_list,
});

export const enregistrerDashboardsFavoris = (favorite_dashboards_list) => ({
  type: actionTypes.ENREGISTRER_DASHBOARD_FAVORI,
  favorite_dashboards_list,
});

export const enregistrerPlansFavoris = (favorite_views_list) => ({
  type: actionTypes.ENREGISTRER_PLAN_FAVORI,
  favorite_views_list,
});

export const enregistrerAccueilSummary = (summary) => ({
  type: actionTypes.ENREGISTRER_ACCUEIL_SUMMARY,
  summary,
});

export const chargementAccueil = (loading) => ({
  type: actionTypes.CHARGEMENT_ACCUEIL,
  loading,
});

const toNumericId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const extractFavoriteIds = (response, idKey) => {
  const favorites = response?.data?.favorites || [];
  return favorites
    .map((favorite) => toNumericId(favorite?.[idKey]))
    .filter((id) => id !== null);
};

const extractFavoriteViews = (response) => {
  const favorites = response?.data?.favorites || [];
  return favorites
    .map((favorite) => ({
      view_id: toNumericId(favorite?.viewId),
      view_name: favorite?.view?.name || '',
      view_title: favorite?.view?.viewTitle || '',
      view_type: toNumericId(favorite?.view?.viewType),
      project_id: toNumericId(favorite?.view?.projectId),
      is_main_view: favorite?.view?.isMainView ? 1 : 0,
      is_visible: favorite?.view?.isVisible ? 1 : 0,
    }))
    .filter((view) => view.view_id !== null);
};

export const reqRecupererAccueilSummary = () => {
  return async (dispatch, getState) => {
    dispatch(chargementAccueil(true));

    const userId = getState()?.authentifie?.user_id;
    if (!userId) {
      dispatch(chargementAccueil(false));
      return;
    }

    try {
      const [
        summaryResponse,
        favoriteViewsResponse,
        favoriteGraphsResponse,
        favoriteDashboardsResponse,
      ] = await Promise.all([
        getHomeSummaryV25({ userId }),
        getFavoriteViewsV25({ userId }),
        getFavoriteGraphsV25({ userId }),
        getFavoriteDashboardsV25({ userId }),
      ]);

      const favorite_views_list = extractFavoriteIds(
        favoriteViewsResponse,
        'viewId'
      );
      const favorite_graphs_list = extractFavoriteIds(
        favoriteGraphsResponse,
        'itemId'
      );
      const favorite_dashboards_list = extractFavoriteIds(
        favoriteDashboardsResponse,
        'viewId'
      );

      // Enrichir les favoris avec view_type depuis le summary (l'API favorites ne retourne pas view_type)
      const summaryViews = summaryResponse?.data?.views || [];
      const viewTypeById = summaryViews.reduce((acc, view) => {
        const id = toNumericId(view.view_id);
        if (id !== null) acc[id] = toNumericId(view.view_type);
        return acc;
      }, {});
      const enrichViewType = (view) => {
        if (view.view_type !== null && view.view_type !== undefined)
          return view;
        const typeFromSummary = viewTypeById[view.view_id];
        return typeFromSummary != null
          ? { ...view, view_type: typeFromSummary }
          : view;
      };

      const favorite_views = extractFavoriteViews(favoriteViewsResponse).map(
        enrichViewType
      );
      const favorite_dashboards = extractFavoriteViews(
        favoriteDashboardsResponse
      ).map(enrichViewType);

      // Dispatch favorites immediately to tab reducers (before slow graph data fetch)
      dispatch({
        type: actionTypes.ENREGISTRER_LISTE_GRAPHES_FAVORIS,
        favorite_graphs_list,
      });
      dispatch({
        type: actionTypes.ENREGISTRER_LISTE_VUES_FAVORIS,
        favorite_views_list: favorite_views_list.map((id) => [id]),
      });

      // Fetch graph data for each favorite graph (or all summary graphs if no favorites)
      const graphDataMap = {};
      const rawSummaryGraphs = summaryResponse?.data?.graphs || [];
      const graphIdsToFetch =
        favorite_graphs_list.length > 0
          ? favorite_graphs_list
          : rawSummaryGraphs
              .map((g) => toNumericId(g?.graph_id))
              .filter((id) => id !== null);

      if (graphIdsToFetch.length > 0) {
        const graphDataResults = await Promise.allSettled(
          graphIdsToFetch.map((graphId) =>
            getGraphData({ graph_id: String(graphId) })
          )
        );
        graphDataResults.forEach((result, index) => {
          if (result.status === 'fulfilled' && result.value?.data?.state) {
            const graphData = result.value.data;
            graphDataMap[graphIdsToFetch[index]] = graphData;
            dispatch({
              type: actionTypes.ENREGISTRER_DONNEES_GRAPHE,
              donnees_graphe: graphData,
            });
          }
        });
      }

      const response = summaryResponse;
      if (response?.data?.state) {
        const rawGraphs = response.data?.graphs || [];
        const summaryGraphIds = new Set(
          rawGraphs
            .map((g) => toNumericId(g?.graph_id))
            .filter((id) => id !== null)
        );

        // Enrichir les graphes du summary avec les données fetchées
        const enrichedGraphs = rawGraphs.map((graph) => {
          const extra =
            graphDataMap[graph.graph_id] ??
            graphDataMap[parseInt(graph.graph_id)];
          return extra
            ? { ...graph, config: extra.config, data: extra.data }
            : graph;
        });

        // Ajouter les graphes favoris absents du summary (avec leurs données fetchées)
        favorite_graphs_list.forEach((graphId) => {
          if (!summaryGraphIds.has(graphId)) {
            const extra = graphDataMap[graphId];
            if (extra) {
              enrichedGraphs.push({
                graph_id: graphId,
                graph_name: extra.config?.title || `Graphe ${graphId}`,
                config: extra.config,
                data: extra.data,
              });
            }
          }
        });

        dispatch(
          enregistrerAccueilSummary({
            ...response.data,
            graphs: enrichedGraphs,
            favorite_views_list,
            favorite_views,
            favorite_graphs_list,
            favorite_dashboards_list,
            favorite_dashboards,
          })
        );
        if (enrichedGraphs.length > 0) {
          dispatch({
            type: actionTypes.ENREGISTRER_LISTE_GRAPHES,
            graphs_list: enrichedGraphs,
          });
        }
      }
    } catch (err) {
      console.error('Erreur getHomeSummaryV25', err);
    } finally {
      dispatch(chargementAccueil(false));
    }
  };
};

// Compares two arrays by JSON serialization (order-independent for objects by key)
const summarizeKey = (arr) => JSON.stringify(arr ?? []);

export const reqVerifierMisesAJourAccueil = () => {
  return async (dispatch, getState) => {
    const state = getState();
    const userId = state?.authentifie?.user_id;
    if (!userId) return;

    const currentSummary = state?.accueil?.summary;

    try {
      const response = await getHomeSummaryV25({ userId });
      if (!response?.data?.state) return;

      const newData = response.data;

      // Compare key fields to detect changes
      const hasChanged =
        summarizeKey(newData.alarmes) !==
          summarizeKey(currentSummary?.alarmes) ||
        summarizeKey(newData.rapports) !==
          summarizeKey(currentSummary?.rapports) ||
        summarizeKey(newData.projects) !==
          summarizeKey(currentSummary?.projects) ||
        summarizeKey(newData.views) !== summarizeKey(currentSummary?.views) ||
        summarizeKey(newData.graphs) !== summarizeKey(currentSummary?.graphs);

      if (hasChanged) {
        // Preserve existing favorites data from current state
        dispatch(
          enregistrerAccueilSummary({
            ...newData,
            favorite_views_list: currentSummary?.favorite_views_list ?? [],
            favorite_views: currentSummary?.favorite_views ?? [],
            favorite_graphs_list: currentSummary?.favorite_graphs_list ?? [],
            favorite_dashboards_list:
              currentSummary?.favorite_dashboards_list ?? [],
            favorite_dashboards: currentSummary?.favorite_dashboards ?? [],
          })
        );

        // Re-fetcher les données des graphes si les graphes ont changé
        if (
          summarizeKey(newData.graphs) !== summarizeKey(currentSummary?.graphs)
        ) {
          const favoriteGraphIds = currentSummary?.favorite_graphs_list ?? [];
          const graphsToRefresh =
            favoriteGraphIds.length > 0
              ? favoriteGraphIds
              : (newData.graphs || [])
                  .map((g) => parseInt(g?.graph_id, 10))
                  .filter(Boolean);

          graphsToRefresh.forEach((graphId) => {
            dispatch(reqRecupererDonneesGraphe(graphId));
          });
        }
      }
    } catch (err) {
      console.error('Erreur polling AccueilSummary', err);
    }
  };
};
